<template>
    <div class="container mx-auto">
        <!-- logo -->
        <div class="flex flex-col items-center justify-center">
            <img class="w-56" src="../assets/logo-megaluzz.png" alt="Megaluzz Logo">
        </div>

        <div class="flex justify-center mb-6" v-if="loading">
            <Loading text="Carregando inscrição... ..." :show="loading" />
        </div>

        <!-- Invalid / expired link -->
        <div v-if="!loading && invalid" class="flex flex-col items-center justify-center mt-12 gap-y-4">
            <h1 class="text-gray-600 font-bold text-xl sm:text-2xl text-center">Este link de inscrição é inválido ou está expirado</h1>
            <p class="text-gray-500 text-center">Entre em contato com a Megaluzz para receber um novo link.</p>
        </div>

        <div v-if="!loading && !invalid">
            <!-- Header -->
            <div class="mt-4 rounded-xl bg-gradient-to-r from-green-600 to-green-500 text-white p-6 shadow">
                <span class="inline-block bg-green-700 text-white text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full">Treinamento</span>
                <h1 class="mt-3 text-2xl sm:text-3xl font-bold leading-tight">{{ event.theme }}</h1>

                <div class="mt-4 flex flex-wrap gap-x-8 gap-y-3">
                    <div v-if="event.instructor">
                        <div class="text-xs uppercase tracking-wide text-green-100">Instrutor</div>
                        <div class="font-semibold">{{ event.instructor }}</div>
                    </div>
                    <div v-if="event.event_date">
                        <div class="text-xs uppercase tracking-wide text-green-100">Data</div>
                        <div class="font-semibold">{{ fmtDate(event.event_date) }}</div>
                    </div>
                    <div v-if="event.event_date">
                        <div class="text-xs uppercase tracking-wide text-green-100">Hora</div>
                        <div class="font-semibold">{{ fmtTime(event.event_date) }}</div>
                    </div>
                    <div v-if="event.workload_hours">
                        <div class="text-xs uppercase tracking-wide text-green-100">Carga horária</div>
                        <div class="font-semibold">{{ event.workload_hours }} hora(s)</div>
                    </div>
                </div>
            </div>

            <p class="text-center text-green-700 font-bold text-lg mt-6">Preencha os campos a seguir para confirmar sua inscrição:</p>

            <div v-for="field in form.structure" :key="field.uuid" class="flex bg-white p-4 rounded-md mt-4 border border-gray-300">
                <div class="flex flex-col flex-1">
                    <!-- LABEL -->
                    <h1 v-if="field.type !== 'static_text'" class="text-green-600 mb-2 font-bold text-base">
                        {{ field.label }}
                        <small v-if="field.required" class="text-red-500">*</small>
                    </h1>
                    <p v-else class="text-gray-700 text-base whitespace-pre-line">{{ field.label }}</p>

                    <!-- TEXT (CPF com máscara) -->
                    <BaseInput
                        v-if="field.type === 'text' && field.data_type === 'cpf'"
                        v-model="field.answer"
                        placeholder="000.000.000-00"
                        v-maska="'###.###.###-##'"
                    />
                    <!-- TEXT normal -->
                    <BaseInput
                        v-if="field.type === 'text' && field.data_type !== 'cpf'"
                        v-model="field.answer"
                    />

                    <!-- EMAIL -->
                    <BaseInput v-if="field.type === 'email'" type="email" class="flex-1" v-model="field.answer" />
                    <div v-if="field.type === 'email' && event.emits_certificate" class="mt-2 flex items-start gap-x-2 rounded-md bg-yellow-50 border border-yellow-300 text-yellow-800 px-3 py-2 text-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mt-0.5 flex-shrink-0 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                        </svg>
                        <span>Informe seu e-mail corretamente, é nele que o certificado será enviado.</span>
                    </div>

                    <!-- LONG TEXT -->
                    <BaseTextarea v-if="field.type === 'long_text'" rows="5" v-model="field.answer" />

                    <!-- MULTIPLE CHOICE (radio) -->
                    <div class="flex flex-col gap-y-4 items-center" v-if="field.type === 'multiple_choice' || field.type === 'scale'">
                        <div class="flex flex-wrap gap-x-6 gap-y-4 mt-4">
                            <div v-for="option in field.options" :key="option.uuid" class="flex items-center gap-x-1">
                                <BaseRadio :value="option.label" :label="option.label" :name="field.uuid" v-model="field.answer" />
                            </div>
                        </div>
                    </div>

                    <!-- CHECKBOX -->
                    <div class="flex flex-col gap-y-4" v-if="field.type === 'checkbox'">
                        <div class="flex flex-col gap-x-2 mt-4" v-for="option in field.options" :key="option.uuid">
                            <div class="flex gap-x-2">
                                <BaseCheckbox :id="option.uuid" :label="option.label" v-model="option.answer" />
                            </div>
                        </div>
                    </div>

                    <!-- SELECT -->
                    <div class="flex flex-col gap-y-4" v-if="field.type === 'select'">
                        <BaseSelect :options="parseSelectOptions(field.options)" v-model="field.answer" class="w-6/12" />
                    </div>

                    <p v-if="field.type !== 'static_text' && invalidFields.has(field.uuid)" class="text-red-500 text-sm mt-1">Este campo é obrigatório</p>
                </div>
            </div>

            <div class="flex justify-center mt-6 mb-12">
                <button :disabled="loadingSubmit" @click="submitForm" class="bg-green-600 text-white px-6 py-4 rounded-md disabled:bg-gray-400 hover:bg-green-700 w-full">
                    <div class="flex gap-x-2 justify-center">
                        <svg v-if="loadingSubmit" class="animate-spin mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Confirmar inscrição
                    </div>
                </button>
            </div>
        </div>
    </div>
</template>

<script>

import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Swal from 'sweetalert';
import { EventService } from '../services/Entity';

export default {
    name: 'EventRegister',
    setup() {
        const route = useRoute();
        const router = useRouter();
        const loading = ref(false);
        const invalid = ref(false);
        const event = ref({});
        const form = ref({ type: 'event', structure: [] });
        const invalidFields = ref(new Set());
        const loadingSubmit = ref(false);

        onMounted(async () => {
            const eventUuid = route.params.event_uuid || null;

            if (!eventUuid) {
                invalid.value = true;
                return;
            }

            try {
                loading.value = true;
                const { data } = await EventService.getPublic(eventUuid);

                event.value = data.event || {};
                form.value = data.form || { type: 'event', structure: [] };

                if (!form.value.structure) {
                    form.value.structure = [];
                }

                // Normalize checkbox answers to boolean
                form.value.structure.forEach(field => {
                    if (field.type === 'checkbox' && Array.isArray(field.options)) {
                        field.options.forEach(opt => {
                            opt.answer = !!opt.answer;
                        });
                    }
                });
            } catch (err) {
                console.log(err);
                invalid.value = true;
            } finally {
                loading.value = false;
            }
        });

        async function submitForm() {
            invalidFields.value.clear();
            let hasError = false;

            form.value.structure.forEach(field => {
                if (field.required) {
                    if (field.type === 'static_text') {
                        return;
                    }
                    if (field.type === 'checkbox') {
                        const checked = (field.options || []).some(opt => opt.answer === true);
                        if (!checked) {
                            invalidFields.value.add(field.uuid);
                            hasError = true;
                        }
                    } else if (!field.answer || field.answer === '') {
                        invalidFields.value.add(field.uuid);
                        hasError = true;
                    }
                }
            });

            if (hasError) {
                Swal({
                    title: 'Formulário incompleto',
                    text: 'Preencha os campos obrigatórios destacados em vermelho.',
                    icon: 'warning'
                });
                return;
            }

            try {
                loadingSubmit.value = true;
                await EventService.register(route.params.event_uuid, { form_structure: form.value });
                loadingSubmit.value = false;
                router.push({ name: 'eventThanks' });
            } catch (err) {
                loadingSubmit.value = false;
                console.log(err);
                Swal({
                    title: 'Erro ao enviar',
                    text: 'Ocorreu um erro ao registrar sua inscrição. Tente novamente mais tarde.',
                    icon: 'error'
                });
            }
        }

        function parseSelectOptions(options = []) {
            return options.map(opt => ({
                title: opt.label,
                value: opt.label
            }));
        }

        function fmtDate(value) {
            if (!value) return '--';
            return new Date(value).toLocaleDateString('pt-BR');
        }

        function fmtTime(value) {
            if (!value) return '--';
            return new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        }

        return {
            loading,
            invalid,
            event,
            form,
            invalidFields,
            loadingSubmit,
            submitForm,
            parseSelectOptions,
            fmtDate,
            fmtTime
        };
    }
}
</script>
